/**
 * VacationModeCard — opt into the 7:00 AM local push digest.
 *
 * When enabled, TurfPulse pushes a one-line summary at 7 AM local time
 * recapping overnight engine activity (fires/skips/runtime/soil VWC).
 * Companion to the 4:30 AM Super's Brief email — same data, phone-first
 * delivery for users on vacation who skim notifications, not inboxes.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane } from 'lucide-react';
import { API_URL } from '../config/api';

const ENDPOINT = `${API_URL}/api/profile/vacation-mode`;

export const VacationModeCard = () => {
  const [enabled, setEnabled] = useState(false);
  const [tz, setTz] = useState(() => {
    try {
      return Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || 'America/Chicago';
    } catch {
      return 'America/Chicago';
    }
  });
  const [lastSent, setLastSent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(ENDPOINT, { withCredentials: true });
      setEnabled(!!res.data?.enabled);
      if (res.data?.timezone) setTz(res.data.timezone);
      setLastSent(res.data?.last_sent_at || null);
    } catch (e) {
      // Stay hidden quietly on auth/network failure
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const browserTz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
      await axios.put(
        ENDPOINT,
        { enabled: next, timezone: browserTz || tz },
        { withCredentials: true },
      );
      setEnabled(next);
      if (browserTz) setTz(browserTz);
      toast.success(next
        ? `Vacation Mode ON — 7 AM digest will land in your push notifications.`
        : 'Vacation Mode OFF');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update Vacation Mode');
    } finally {
      setSaving(false);
    }
  };

  const lastSentLabel = (() => {
    if (!lastSent) return null;
    try {
      const d = new Date(lastSent);
      return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch { return null; }
  })();

  return (
    <div
      data-testid="vacation-mode-card"
      className="bg-slate-900/60 border border-slate-800 rounded-2xl p-5 space-y-3"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-sky-500/20 border border-sky-600/40 flex items-center justify-center flex-shrink-0">
          <Plane className="w-5 h-5 text-sky-400" />
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold text-slate-100">Vacation Mode</div>
          <div className="text-xs text-slate-400 mt-0.5 leading-snug">
            7 AM local push digest recapping overnight fires, skips, runtime, and
            current soil moisture. Mobile companion to the 4:30 AM Super&apos;s Brief
            email.
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid="vacation-mode-toggle"
          disabled={saving}
          onClick={() => toggle(!enabled)}
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition ${
            enabled
              ? 'bg-emerald-600 hover:bg-emerald-500 border-emerald-700 text-white'
              : 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-200'
          } disabled:opacity-50`}
        >
          {enabled ? 'ON · 7 AM digest enabled' : 'Enable 7 AM digest'}
        </button>
        <div className="text-[11px] text-slate-500">tz: <span className="text-slate-400 font-mono">{tz}</span></div>
      </div>

      {enabled && lastSentLabel && (
        <div className="text-[11px] text-slate-500" data-testid="vacation-mode-last-sent">
          Last digest sent: <span className="text-slate-300">{lastSentLabel}</span>
        </div>
      )}
      {enabled && !lastSentLabel && (
        <div className="text-[11px] text-slate-500" data-testid="vacation-mode-pending">
          Next digest will land at 7:00 AM in your timezone.
        </div>
      )}
    </div>
  );
};

export default VacationModeCard;
